'use client';

import {
  matchesPublicCategoryProductsSeed,
  type PublicCategoryProductsSeed,
} from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import {
  fetchProductsWithSignal,
  productsInfiniteQueryKey,
  retryProductsQuery,
} from '@eliadmin/core/src/api/products/products-query';
import {
  categoryProductsQueryOptions,
  normalizeCategoryPage,
} from '@eliadmin/core/src/components/categories/category-query-state';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { GenericError } from '@eliadmin/core/src/components/error/generic-error';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filters = useCategoryStore(s => s.filter);
  const paramsReady = useCategoryStore(s => s.paramsReady);
  const storedPage = useCategoryStore(s => s.page);
  const setPage = useCategoryStore(s => s.setPage);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const atomicParams = paramsReady !== undefined && typeof setPage === 'function';
  const currentPage = normalizeCategoryPage(storedPage ?? searchParams.get('page'));
  const queryReady = isCustomerInitialized !== false && paramsReady !== false;

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = buildProductsQuery(
    categoryProductsQueryOptions(
      props.category.id,
      generalLayout?.pagination?.paginationProducts || 12,
      filters,
    ),
  );

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage,
  }).uri;
  const productResetUri = buildProductsRequest({
    ...{
      ...queryWithoutPage,
      page: currentPage,
    },
    page: undefined,
  }).uri;

  const productQueryKey = productsInfiniteQueryKey(
    locale,
    productRequestUri,
    authToken,
    currentPage,
  );
  const hasSeed = matchesPublicCategoryProductsSeed(props.initialProducts, {
    locale,
    requestUri: productRequestUri,
    authToken,
    page: currentPage,
  });

  const productsQuery = useInfiniteQuery({
    initialData: hasSeed
      ? { pages: [props.initialProducts!.data], pageParams: [currentPage] }
      : undefined,
    initialDataUpdatedAt: hasSeed ? props.initialProducts?.updatedAt : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: retryProductsQuery,
    staleTime: 5 * 60 * 1000,
    queryKey: productQueryKey,
    queryFn: ({ pageParam, signal }) => {
      return fetchProductsWithSignal({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
        signal,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    enabled: hasSeed || queryReady,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const isPending = !productsQuery.isError && !productsQuery.data;

  useEffect(() => {
    if (typeof lastPage?.meta?.total === 'number') {
      props.onProductTotalChange?.(lastPage.meta.total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  return (
    <div
      aria-busy={isPending}
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-6 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
      )}
    >
      {!isPending && !productsQuery.isError && !flatList.length && (
        <p className="text-center text-muted-foreground">{t('text554')}</p>
      )}

      {!!flatList.length && (
        <CollectionPageSchema
          category={props.category}
          products={flatList}
          order={filters.sort.order ?? 'desc'}
          url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
          currency={generalLayout?.currency_code || generalLayout?.currency}
        />
      )}

      {productsQuery.isError && (
        <GenericError
          error={productsQuery.error}
          reset={() => {
            if (queryReady) void productsQuery.refetch();
          }}
        />
      )}

      <ProductsGrid
        products={flatList ?? []}
        emitSchema={false}
        className={{
          wrapper: cn(
            'grid w-full grid-cols-2 @lg:grid-cols-2 @2xl:grid-cols-3',
            'ea-category-products__grid',
            props.className?.grid_wrapper,
          ),
          product: {
            main: cn(props.className?.product),
          },
        }}
      />

      {!!productsQuery.hasNextPage && (
        <div className={cn('flex flex-row justify-center', 'ea-category-products__load-more')}>
          <Button
            variant="secondary"
            isLoading={productsQuery.isFetchingNextPage}
            onClick={() => {
              if (queryReady && !productsQuery.isFetchingNextPage)
                void productsQuery.fetchNextPage();
            }}
          >
            {t('text338')}
          </Button>
        </div>
      )}

      <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
        <Pagination
          shouldReplace={atomicParams}
          setPage={atomicParams ? setPage : undefined}
          links={lastPage?.meta.links}
          onPageClicked={page => {
            if (String(page) !== String(currentPage)) return;
            queryClient.resetQueries({
              queryKey: productQueryKey,
              exact: true,
            });
          }}
          scrollToElementAfterPageChange={
            props.paginationProps?.scrollToElementAfterPageChange || undefined
          }
          resetWhen={atomicParams ? undefined : [productResetUri]}
        />
      </div>

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-category-products__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid w-full gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('min-h-[400px] min-w-[250px]', className?.item)} />
        ))}
      </div>
      <Skeleton className="h-20 w-full" />
    </div>
  );
}
